import { TandemEvent, ValidTandemEvent } from "@/lib/drivers/tandem/commands/Event";
import { BolexActivatedEvent, LID_BOLEX_ACTIVATED } from "@/lib/drivers/tandem/events/LID_BOLEX_ACTIVATED";
import { BolexCompletedEvent, LID_BOLEX_COMPLETED } from "@/lib/drivers/tandem/events/LID_BOLEX_COMPLETED";
import { BolusActivatedEvent, LID_BOLUS_ACTIVATED } from "@/lib/drivers/tandem/events/LID_BOLUS_ACTIVATED";
import { BolusCompletedEvent, LID_BOLUS_COMPLETED } from "@/lib/drivers/tandem/events/LID_BOLUS_COMPLETED";
import { BolusDeliveryEvent, LID_BOLUS_DELIVERY } from "@/lib/drivers/tandem/events/LID_BOLUS_DELIVERY";
import { BolusRequested1Event, LID_BOLUS_REQUESTED_MSG1 } from "@/lib/drivers/tandem/events/LID_BOLUS_REQUESTED_MSG1";
import { BolusRequested2Event, LID_BOLUS_REQUESTED_MSG2 } from "@/lib/drivers/tandem/events/LID_BOLUS_REQUESTED_MSG2";
import { BolusRequested3Event, LID_BOLUS_REQUESTED_MSG3 } from "@/lib/drivers/tandem/events/LID_BOLUS_REQUESTED_MSG3";
import { TandemData } from "@/lib/drivers/tandem/TandemTypes";
import { TandemTransformer } from "@/lib/drivers/tandem/transformers/TandemTransformer";
import { BolusInsulinDTO, BolusSettingsDTO } from "@/models/InsulinModel";

type BolusEventType =
  | BolusRequested1Event
  | BolusRequested2Event
  | BolusRequested3Event
  | BolusActivatedEvent
  | BolusCompletedEvent
  | BolusDeliveryEvent
  | BolexActivatedEvent
  | BolexCompletedEvent;

type BolusEvent = {
  msg1: ValidTandemEvent<BolusRequested1Event>;
  msg2: ValidTandemEvent<BolusRequested2Event>;
  msg3: ValidTandemEvent<BolusRequested3Event>;
  bolusActivated: ValidTandemEvent<BolusActivatedEvent>;
  bolusCompleted: ValidTandemEvent<BolusCompletedEvent>;
  bolusDelivered: ValidTandemEvent<BolusDeliveryEvent>[];
  bolexActivated?: ValidTandemEvent<BolexActivatedEvent>;
  bolexCompleted?: ValidTandemEvent<BolexCompletedEvent>;
};

export class TandemBolusInsulinTransformer implements TandemTransformer<BolusInsulinDTO[]> {
  _is_bolus_request_msg1_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusRequested1Event> =>
    event.id === LID_BOLUS_REQUESTED_MSG1.value;

  _is_bolus_request_msg2_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusRequested2Event> =>
    event.id === LID_BOLUS_REQUESTED_MSG2.value;

  _is_bolus_request_msg3_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusRequested3Event> =>
    event.id === LID_BOLUS_REQUESTED_MSG3.value;

  _is_bolus_activated_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusActivatedEvent> =>
    event.id === LID_BOLUS_ACTIVATED.value;

  _is_bolus_completed_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusCompletedEvent> =>
    event.id === LID_BOLUS_COMPLETED.value;

  _is_bolus_delivered_event = (event: TandemEvent<unknown>): event is TandemEvent<BolusDeliveryEvent> =>
    event.id === LID_BOLUS_DELIVERY.value;

  _is_bolex_activated_event = (event: TandemEvent<unknown>): event is TandemEvent<BolexActivatedEvent> =>
    event.id === LID_BOLEX_ACTIVATED.value;

  _is_bolex_completed_event = (event: TandemEvent<unknown>): event is TandemEvent<BolexCompletedEvent> =>
    event.id === LID_BOLEX_COMPLETED.value;

  _is_valid_bolus_event = (event: Partial<BolusEvent>): event is BolusEvent => {
    return !!(
      event.msg1 &&
      event.msg2 &&
      event.msg3 &&
      event.bolusActivated &&
      event.bolusCompleted &&
      event.bolusDelivered &&
      event.bolusDelivered.length > 0
    );
  };

  bolus_insulin = (events: BolusEvent[]): BolusInsulinDTO[] => {
    const insulin: BolusInsulinDTO[] = [];
    for (const event of events) {
      const hasCarbs = !!event.msg1.payload.carbAmount;
      const hasFoodBolus = !!event.msg3.payload.foodBolusSize;
      const foodBolusMethod = hasCarbs && hasFoodBolus ? "carbs" : hasFoodBolus ? "manual" : "none";
      const hasCorrection = !!event.msg3.payload.correctionBolusSize;
      const bolusOption =
        event.msg2.payload.bolusOption === "ble_standard" ? "standard" : event.msg2.payload.bolusOption;

      const bolusSettings: BolusSettingsDTO = {
        bolus_id: event.msg1.payload.bolusId,
        food_insulin_input_method: foodBolusMethod,
        food_insulin: event.msg3.payload.foodBolusSize ?? 0,
        correction_insulin_input_method: hasCorrection ? "blood_glucose" : "none",
        correction_insulin: event.msg3.payload.correctionBolusSize ?? 0,
        total_insulin: event.msg3.payload.totalBolusSize ?? 0,

        carbs: event.msg1.payload.carbAmount,
        carbs_ratio: event.msg1.payload.carbRatio,
        glycemia: event.msg1.payload.bg,
        insulin_sensitivity: event.msg2.payload.isf,
        target_glycemia: event.msg2.payload.targetBg,
        iob: event.msg1.payload.iob,
        percentage_delivered_now: event.msg2.payload.standardPercent,
        bolus_insulin: event.bolusActivated?.payload.bolusSize,
        bolex_insulin: event.bolexActivated?.payload.bolexSize,
        bolex_duration: event.msg2.payload.duration,
        bolus_option: bolusOption,
      };

      const bolusDelivered = event.bolusCompleted.payload.insulinDelivered ?? 0;
      const bolexDelivered = event.bolexCompleted?.payload.bolexInsulinDelivered ?? 0;

      const bolusRequested = event.bolusCompleted.payload.insulinRequested ?? 0;
      const bolexRequested = event.bolexCompleted?.payload.bolexInsulinRequested ?? 0;

      const deliveredInsulin = bolusDelivered + bolexDelivered;

      const bolus: BolusInsulinDTO = {
        sequence_id: event.bolusCompleted.eventSequenceNum,
        quantity: deliveredInsulin,
        date: event.bolusActivated.timestamp.toISO() ?? "",
        type: "short",
        reason: "bolus",
        bolus_settings: bolusSettings,
        delivered_insulin: deliveredInsulin,
        requested_insulin: bolusRequested + bolexRequested,
      };
      insulin.push(bolus);
    }
    return insulin;
  };

  merge_into_bolus_event = (events: TandemEvent<BolusEventType>[]) => {
    const eventsMap: Record<number, Partial<BolusEvent>> = {};
    for (const event of events) {
      if (event.valid && event.payload.bolusId) {
        if (this._is_bolus_request_msg1_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            msg1: event,
          };
        } else if (this._is_bolus_request_msg2_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            msg2: event,
          };
        } else if (this._is_bolus_request_msg3_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            msg3: event,
          };
        } else if (this._is_bolus_activated_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            bolusActivated: event,
          };
        } else if (this._is_bolus_completed_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            bolusCompleted: event,
          };
        } else if (this._is_bolus_delivered_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            bolusDelivered: [...(eventsMap[event.payload.bolusId]?.bolusDelivered ?? []), event],
          };
        } else if (this._is_bolex_activated_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            bolexActivated: event,
          };
        } else if (this._is_bolex_completed_event(event)) {
          eventsMap[event.payload.bolusId] = {
            ...eventsMap[event.payload.bolusId],
            bolexCompleted: event,
          };
        }
      }
    }

    return Object.values(eventsMap).filter(this._is_valid_bolus_event);
  };

  transform = (data: TandemData): BolusInsulinDTO[] => {
    const bolusEvents: TandemEvent<BolusEventType>[] = data.events.events.filter(
      e =>
        this._is_bolus_request_msg1_event(e) ||
        this._is_bolus_request_msg2_event(e) ||
        this._is_bolus_request_msg3_event(e) ||
        this._is_bolus_activated_event(e) ||
        this._is_bolus_completed_event(e) ||
        this._is_bolus_delivered_event(e) ||
        this._is_bolex_activated_event(e) ||
        this._is_bolex_completed_event(e),
    );
    const bolusEventsGrouped = this.merge_into_bolus_event(bolusEvents);
    return this.bolus_insulin(bolusEventsGrouped);
  };
}
